import * as prismicH from '@prismicio/helpers';

import { linkResolver } from '../prismicio';
import { showDatePeriodDay } from '../lib/date_helpers';
import VisTimeline from './VisTimeline';

function ProjectCalendarTimeline({ projects, hideName }) {
  // the date range boundaries fit all project's start to end dates, plus/minus 3 months
  const minDate = projects
    .map((p) => new Date(p.data.start_date || p.data.year))
    .sort((a, b) => a - b)[0];
  if (minDate) minDate.setMonth(minDate.getMonth() - 3);
  const maxDate = projects
    .map((p) => new Date(p.data.end_date || p.data.year))
    .sort((a, b) => b - a)[0];
  if (maxDate) maxDate.setMonth(maxDate.getMonth() + 3);
  // the date range initially shown is a year, starting 3 months ago
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 3);
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + 12);

  const groups = projects.flatMap((project) => [
    { id: `${project.id}-title`, content: '', className: 'title' },
    { id: project.id, content: '' }
  ]);

  const items = projects
    // timeline items
    .flatMap((project) =>
      project.data.timeline_items.map((item, idx) => ({
        id: `${project.id}-${idx}`,
        content: timelineItemTitle(item),
        start: new Date(item.start_date),
        end: item.end_date && new Date(item.end_date),
        group: project.id,
        type: item.end_date ? 'range' : 'point',
        className:
          (project.data.dates_tentative || item.tentative) && 'tentative',
        tentative: project.data.dates_tentative || item.tentative,
        item,
        project
      }))
    )
    .concat(
      // project names
      (hideName ? [] : projects).map((project) => ({
        id: `${project.id}-title`,
        content: projectHeading(project),
        start: minDate,
        end: maxDate,
        group: `${project.id}-title`,
        type: 'range',
        className: project.data.dates_tentative ? 'title tentative' : 'title',
        tentative: project.data.dates_tentative,
        project,
        tooltip: false
      }))
    )
    .concat(
      // project periods (as background)
      projects
        .filter((p) => p.data.start_date && p.data.end_date)
        .map((project) => ({
          id: `${project.id}-period`,
          start: new Date(project.data.start_date),
          end: project.data.end_date && new Date(project.data.end_date),
          group: project.id,
          type: 'background',
          className: project.data.dates_tentative && 'tentative',
          tentative: project.data.dates_tentative,
          project
        }))
    );

  return (
    <VisTimeline
      groups={groups}
      items={items}
      min={minDate}
      start={startDate}
      end={endDate}
      max={maxDate}
      zoomKey="ctrlKey"
      groupHeightMode="fixed"
      timeAxis={{ scale: 'month', step: 1 }}
      zoomMin={3 * 30 * 24 * 60 * 60 * 1000} /* 3 months */
      orientation={{ axis: 'top' }}
      tooltip={{
        followMouse: true,
        overflowMethod: 'cap',
        delay: 20,
        template: timelineItemTooltip
      }}
      selectable={false}
      stack
      horizontalScroll
    />
  );
}

function projectHeading(project) {
  const url = linkResolver(project);
  const title = prismicH.asText(project.data.title);
  const tentative = project.data.dates_tentative ? ' <em>(tentative)</em>' : '';
  return `<a href="${url}"><b>${title}</b></a>${tentative}`;
}

const timelineIcons = {
  consultation: '/images/timeline/message.svg',
  data: '/images/timeline/database-backup.svg',
  methodology: '/images/timeline/clipboard-list.svg',
  report: '/images/timeline/file-text.svg',
  symposium: '/images/timeline/people-line.svg'
};

function timelineItemTitle(item) {
  if (!item.icon || item.icon === '-') {
    return item.title;
  } else {
    const src = timelineIcons[item.icon];
    return `<img src="${src}" height="14" alt=""> ${item.title}`;
  }
}

function timelineItemTooltip(item, element) {
  if (element.tooltip === false) return null;
  if (typeof element.item !== 'object') return null;

  const period = showDatePeriodDay(element.start, element.end);
  const tentative = element.tentative ? ' <em>(tentative)</em>' : '';
  return [
    prismicH.asText(element.project.data.title),
    `<strong>${element.item.title}</strong>`,
    period.concat(tentative)
  ].join('<br>');
}

export default ProjectCalendarTimeline;
